import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { formatOrderRequestErrors } from './order_request_client';
import { logError, logInfo } from '../config_and_helpers/logger';
import { isCanceledRequest } from '../utils/listSearchRequest';

const LOG_CONTEXT = 'inventory_product_list_client';

const PREVIEW_PATH = 'inventory/product-list/preview/';
const APPLY_PATH = 'inventory/product-list/apply/';
export const EXPORT_PATH = 'inventory/product-list/export/';

const DEFAULT_EXPORT_FILENAME = 'inventory_products.xlsx';

/**
 * Extracts a filename from `Content-Disposition` (RFC 5987 + plain forms).
 *
 * @param {string|undefined} header
 * @param {string} defaultName
 * @returns {string}
 */
function parseFilenameFromContentDisposition(header, defaultName) {
  if (!header || typeof header !== 'string') return defaultName;
  const utf8Match = header.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // fall through
    }
  }
  const plainMatch = header.match(
    /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i
  );
  if (plainMatch) {
    const value = (plainMatch[1] || plainMatch[2] || '').trim();
    if (value) return value;
  }
  return defaultName;
}

/**
 * Triggers a browser download for an in-memory blob.
 *
 * @param {Blob} blob
 * @param {string} filename
 */
function triggerBlobDownload(blob, filename) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

/**
 * Read blob body as text (jsdom may lack `Blob.prototype.text`).
 *
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
async function readBlobAsText(blob) {
  if (typeof blob.text === 'function') {
    return blob.text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * Best-effort detail from axios blob error bodies (DRF often returns JSON).
 *
 * @param {*} data - `error.response.data` (Blob or plain object)
 * @param {string} fallback
 * @returns {Promise<string>}
 */
export async function parseBlobErrorDetail(data, fallback) {
  if (data == null) return fallback;
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && !(data instanceof Blob)) {
    if (data.detail != null) return String(data.detail);
    if (data.message != null) return String(data.message);
    return fallback;
  }
  if (!(data instanceof Blob)) return fallback;
  try {
    const text = await readBlobAsText(data);
    if (!text) return fallback;
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed === 'string') return parsed;
      if (parsed?.detail != null) return String(parsed.detail);
      if (parsed?.message != null) return String(parsed.message);
    } catch {
      return text.length > 240 ? `${text.slice(0, 240)}…` : text;
    }
  } catch {
    // ignore read failures
  }
  return fallback;
}

/**
 * Summarize API error payloads for logs (avoid large or sensitive dumps).
 *
 * @param {*} errPayload
 * @returns {Object}
 */
function summarizeErrorPayload(errPayload) {
  if (errPayload == null) return { kind: 'empty' };
  if (typeof errPayload === 'string') {
    return { kind: 'string', length: errPayload.length };
  }
  if (Array.isArray(errPayload)) {
    return { kind: 'array', length: errPayload.length };
  }
  if (typeof errPayload === 'object') {
    return { kind: 'object', keys: Object.keys(errPayload).slice(0, 12) };
  }
  return { kind: typeof errPayload };
}

/**
 * User-facing message for export failures.
 *
 * @param {{ status?: number, detail?: string, isNetworkError?: boolean }} result
 * @returns {string}
 */
export const getInventoryExportErrorMessage = result => {
  if (result?.status === 403) {
    return 'You do not have permission to export the inventory list.';
  }
  if (result?.status === 401) {
    return 'Please log in again to export the inventory list.';
  }
  if (result?.isNetworkError) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  return result?.detail || 'Export failed. Please try again.';
};

/**
 * Flatten DRF `file` field errors from preview endpoint.
 * @param {Object|Array|string|null} errPayload
 * @returns {string[]}
 */
export const formatInventoryListFileErrors = errPayload => {
  if (errPayload == null) {
    return ['Upload failed. Please try again.'];
  }
  if (typeof errPayload === 'string') {
    return [errPayload];
  }
  if (Array.isArray(errPayload)) {
    return errPayload.map(item => String(item));
  }
  const fileErrors = errPayload.file;
  if (fileErrors != null) {
    return Array.isArray(fileErrors)
      ? fileErrors.map(String)
      : [String(fileErrors)];
  }
  return formatOrderRequestErrors(errPayload);
};

/**
 * POST /v1/inventory/product-list/preview/ — upload .xlsx and return matched rows.
 *
 * @async
 * @param {string} token - Lab user auth token
 * @param {File} file - `.xlsx` workbook
 * @param {Object} [columnHints] - Optional multipart overrides
 * @param {string} [columnHints.sheet_name]
 * @param {string} [columnHints.cat_num_column]
 * @param {string} [columnHints.name_column]
 * @param {string} [columnHints.quantity_column]
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*,status?:number}>}
 */
export const previewInventoryProductList = async (token, file, columnHints = {}) => {
  try {
    logInfo(LOG_CONTEXT, 'previewInventoryProductList start', {
      fileName: file?.name,
    });
    const formData = new FormData();
    formData.append('file', file);
    if (columnHints.sheet_name) {
      formData.append('sheet_name', columnHints.sheet_name);
    }
    if (columnHints.cat_num_column) {
      formData.append('cat_num_column', columnHints.cat_num_column);
    }
    if (columnHints.name_column) {
      formData.append('name_column', columnHints.name_column);
    }
    if (columnHints.quantity_column) {
      formData.append('quantity_column', columnHints.quantity_column);
    }

    const response = await axios.post(`${BACKEND_URL}${PREVIEW_PATH}`, formData, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    logInfo(LOG_CONTEXT, 'previewInventoryProductList success', {
      totalRows: response.data?.summary?.total_rows,
      matchedCount: response.data?.summary?.matched_count,
    });
    return { success: true, data: response.data };
  } catch (error) {
    const status = error.response?.status;
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'previewInventoryProductList failed', {
      status,
      errorSummary: summarizeErrorPayload(errPayload),
    });
    return {
      success: false,
      status,
      error: errPayload,
      messages: formatInventoryListFileErrors(errPayload),
    };
  }
};

/**
 * POST /v1/inventory/product-list/apply/ — link project and/or create order request.
 *
 * @async
 * @param {string} token
 * @param {Object} payload - Apply body per backend handoff
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*,status?:number}>}
 */
export const applyInventoryProductList = async (token, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'applyInventoryProductList start', {
      rowCount: Array.isArray(payload?.rows) ? payload.rows.length : 0,
      linkToProject: payload?.link_matched_to_project,
      createOrderRequest: payload?.create_order_request,
    });
    const response = await axios.post(`${BACKEND_URL}${APPLY_PATH}`, payload, {
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'application/json',
      },
    });
    logInfo(LOG_CONTEXT, 'applyInventoryProductList success', {
      projectId: response.data?.project?.id,
      linked: response.data?.project_products_linked,
      requestRef: response.data?.order_request?.request_ref,
    });
    return { success: true, data: response.data };
  } catch (error) {
    const status = error.response?.status;
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'applyInventoryProductList failed', {
      status,
      errorSummary: summarizeErrorPayload(errPayload),
    });
    return {
      success: false,
      status,
      error: errPayload,
      messages: formatOrderRequestErrors(errPayload),
    };
  }
};

/**
 * GET /v1/inventory/product-list/export/ — download all inventory products as `.xlsx`.
 *
 * @async
 * @param {string} token - Lab user auth token
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - Optional abort signal
 * @returns {Promise<
 *   | { success: true, filename: string }
 *   | { success: false, status?: number, detail?: string }
 * >}
 */
export const exportInventoryProductList = async (token, options = {}) => {
  const { signal } = options;
  const url = `${BACKEND_URL}${EXPORT_PATH}`;
  try {
    logInfo(LOG_CONTEXT, 'exportInventoryProductList start', { url });
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
      responseType: 'blob',
      signal,
    });
    const contentType =
      response.headers?.['content-type'] ||
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const filename = parseFilenameFromContentDisposition(
      response.headers?.['content-disposition'],
      DEFAULT_EXPORT_FILENAME
    );
    const blob = new Blob([response.data], { type: contentType });
    triggerBlobDownload(blob, filename);
    logInfo(LOG_CONTEXT, 'exportInventoryProductList success', { filename });
    return { success: true, filename };
  } catch (error) {
    if (isCanceledRequest(error)) {
      logInfo(LOG_CONTEXT, 'exportInventoryProductList canceled', { url });
      return { success: false, canceled: true };
    }
    const status = error.response?.status;
    const fallback = error.response?.statusText || error.message;
    const detail = await parseBlobErrorDetail(error.response?.data, fallback);
    const isNetworkError = !status && !error.response;
    logError(LOG_CONTEXT, 'exportInventoryProductList failed', {
      status,
      isNetworkError,
      detail,
      url,
    });
    return { success: false, status, detail, isNetworkError };
  }
};
